import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications/index.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useEffect, useState } from 'react';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useGroupId } from '@trinity/chat-components/containers/Chat/selectors';
import { buildInitialChatContext } from './build-default-context';
import { ChatContextItemsContext, ChatContextItemsLoadingContext, ChatDefaultContextItemContext } from '@trinity/chat-components/components/Chat';

type Props = {
  children: React.ReactNode;
};

export const ChatAgentsContainer = (props: Props) => {

  const brandIdentifier = useActiveBrandIdentifier();
  const applicationIdentifier = useActiveApplicationIdentifier();
  const apiClient = useChatApiClient();
  const location = useLocation();
  const groupId = useGroupId();

  const [defaultContext, setDefaultContext] = useState<BrandInsightsPageContext.Item | null>(null);

  const q = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier, applicationIdentifier],
    queryFn: () => {
      return apiClient.chat.getChatContexts({ brandIdentifier, applicationIdentifier }).then(res => res.items);
    },
    // placeholderData: [],
    enabled: !!brandIdentifier && !!applicationIdentifier,
  });

  useEffect(() => {
    if (q.isSuccess && q.data) {
      setDefaultContext(buildInitialChatContext({
        contextItems: q.data,
        groupId,
        pathname: location.pathname,
      }));
    }
  }, [q.data, q.isSuccess, groupId, location.pathname]);

  const isLoading = q.isLoading;

  if (isLoading || !q.data || !defaultContext) {
    return null;
  }

  return (
    <ChatContextItemsContext.Provider value={q.data}>
      <ChatContextItemsLoadingContext.Provider value={isLoading}>
        <ChatDefaultContextItemContext.Provider value={defaultContext}>
          {props.children}
        </ChatDefaultContextItemContext.Provider>
      </ChatContextItemsLoadingContext.Provider>
    </ChatContextItemsContext.Provider>
  );
};